import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Trophy, TrendingUp, Flame, Zap, Target, CheckCircle2, Lock, Sparkles } from "lucide-react";
import { BETA_MODE } from "@/components/grind/GrindConstants";
import WorkoutGenerator from "@/components/WorkoutGenerator";
import SeasonalFocusCard from "@/components/SeasonalFocusCard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProgramProgress = () => {
  const navigate = useNavigate();
  const [progress, setProgress] = useState(null);
  const [baseline, setBaseline] = useState(null);
  const [phaseTests, setPhaseTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatorOpen, setGeneratorOpen] = useState(false);
  const [generatorPhase, setGeneratorPhase] = useState(1);

  const openGeneratorForPhase = (phase) => {
    setGeneratorPhase(Math.min(phase, 5));
    setGeneratorOpen(true);
  };

  useEffect(() => {
    fetchProgressData();
  }, []);

  const fetchProgressData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [progressRes, baselineRes, testsRes] = await Promise.all([
        axios.get(`${API}/program/progress`, { headers }),
        axios.get(`${API}/baseline`, { headers }),
        axios.get(`${API}/phase-tests`, { headers })
      ]);

      setProgress(progressRes.data);
      setBaseline(baselineRes.data);
      setPhaseTests(testsRes.data);
    } catch (error) {
      console.error('Error fetching progress data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPhaseIcon = (phase) => {
    const icons = { 1: Target, 2: Flame, 3: Zap, 4: TrendingUp };
    return icons[phase] || Target;
  };

  const getPhaseColor = (phase) => {
    const colors = {
      1: 'text-blue-500 border-blue-500/30 bg-blue-500/10',
      2: 'text-secondary border-secondary/30 bg-secondary/10',
      3: 'text-accent border-accent/30 bg-accent/10',
      4: 'text-primary border-primary/30 bg-primary/10'
    };
    return colors[phase] || 'text-white border-border bg-card';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-primary text-xl font-heading">Loading...</div>
      </div>
    );
  }

  if (!progress?.baseline_completed) {
    navigate('/baseline');
    return null;
  }

  const progressPercent = (progress.current_week / 16) * 100;

  return (
    <div className="min-h-screen bg-background p-6 md:p-8 lg:p-12">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-4xl md:text-5xl font-heading font-black text-white tracking-tighter-xl">
            YOUR 112-DAY GRIND
          </h1>
          <p className="text-muted-foreground mt-2">Road to the Season • 16-Week Transformation</p>
        </div>

        {/* Seasonal Context — tells the athlete how this week's work changes with the season */}
        <SeasonalFocusCard />

        {/* Progress Overview */}
        <Card className="bg-card border-border" data-testid="progress-overview">
          <CardContent className="p-6 space-y-6">
            {/* Week Progress Bar */}
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-2xl font-heading font-black text-white">WEEK {progress.current_week} OF 16</h3>
                  <p className="text-sm text-muted-foreground">{progress.days_elapsed} days into your transformation</p>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-heading font-black text-primary">{Math.round(progressPercent)}%</div>
                  <div className="text-xs text-muted-foreground">COMPLETE</div>
                </div>
              </div>
              <Progress value={progressPercent} className="h-3" data-testid="progress-bar" />
            </div>

            {/* Current Phase */}
            {progress.phase_info && (
              <div className={`p-4 rounded-sm border ${getPhaseColor(progress.current_phase)}`}>
                <div className="flex items-center gap-3 mb-2">
                  {React.createElement(getPhaseIcon(progress.current_phase), { className: 'w-6 h-6' })}
                  <div>
                    <h4 className="font-heading font-bold text-lg">PHASE {progress.current_phase}: {progress.phase_info.name}</h4>
                    <p className="text-sm opacity-80">Weeks {progress.phase_info.weeks}</p>
                  </div>
                </div>
                <p className="text-sm mb-2"><span className="font-bold">Goal:</span> {progress.phase_info.goal}</p>
                <p className="text-xs italic">"{progress.phase_info.message}"</p>
              </div>
            )}

            {/* Next Milestone */}
            {progress.next_milestone && (
              <div className="p-4 bg-primary/5 border border-primary/30 rounded-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase mb-1">Next Milestone</p>
                    <p className="font-medium text-white">{progress.next_milestone.description}</p>
                  </div>
                  {progress.next_milestone.type === 'phase_test' && (
                    <Button
                      data-testid="phase-test-button"
                      onClick={() => navigate('/phase-test')}
                      className="bg-primary hover:bg-primary/90 text-white font-bold uppercase"
                    >
                      Take Test
                    </Button>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* The Road - Phase Breakdown */}
        <div className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <h2 className="text-2xl font-heading font-black text-white">THE ROAD TO THE SEASON</h2>
            <Button
              onClick={() => openGeneratorForPhase(progress.current_phase || 1)}
              className="bg-primary hover:bg-primary/90 font-heading font-bold h-11"
              data-testid="quick-generate-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" /> PICK & GENERATE WORKOUT
            </Button>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="phase-cards">
            {[1, 2, 3, 4, 5, 6, 7].map((phase) => {
              const Icon = getPhaseIcon(phase);
              const isCompleted = progress.current_phase > phase;
              const isCurrent = progress.current_phase === phase;
              const isLocked = !BETA_MODE && progress.current_phase < phase;

              const phaseData = {
                1: { name: "Structural Integrity", weeks: "1-4", message: "Build the chassis" },
                2: { name: "Force Production", weeks: "5-8", message: "Increase power ceiling" },
                3: { name: "Explosive Power", weeks: "9-12", message: "Light the fuse" },
                4: { name: "Game Conditioning", weeks: "13-16", message: "4th quarter beast" },
                5: { name: "Pre-Season Prep", weeks: "17-20", message: "Ready for first whistle" },
                6: { name: "In-Season Maintenance", weeks: "Ongoing", message: "Stay sharp all season" },
                7: { name: "Recovery & Regeneration", weeks: "Off-Season", message: "Rest now, dominate later" }
              };

              const hasWorkouts = phase <= 5; // Phases 1-5 have day-by-day workouts in /grind
              const isClickable = !isLocked && hasWorkouts;

              return (
                <Card
                  key={phase}
                  onClick={isClickable ? () => openGeneratorForPhase(phase) : undefined}
                  className={`border-2 transition-all ${
                    isCurrent
                      ? `${getPhaseColor(phase)} border-2`
                      : isCompleted
                      ? 'bg-card border-green-500/50'
                      : isLocked
                      ? 'bg-card/50 border-border opacity-60'
                      : `${getPhaseColor(phase)} opacity-90`
                  } ${isClickable ? 'cursor-pointer hover:scale-[1.02] hover:opacity-100 hover:shadow-lg hover:shadow-primary/20' : ''}`}
                  data-testid={`phase-card-${phase}`}
                >
                  <CardHeader>
                    <div className="flex items-center justify-between mb-2">
                      <div className={`w-12 h-12 rounded-sm border-2 flex items-center justify-center ${
                        isCurrent ? getPhaseColor(phase) : isCompleted ? 'bg-green-500/10 border-green-500/30 text-green-500' : 'bg-card border-border'
                      }`}>
                        {isCompleted ? <CheckCircle2 className="w-6 h-6" /> : isLocked ? <Lock className="w-6 h-6" /> : <Icon className="w-6 h-6" />}
                      </div>
                      <Badge variant={isCurrent ? "default" : "outline"} className="font-bold">
                        PHASE {phase}
                      </Badge>
                    </div>
                    <CardTitle className="text-lg font-heading font-bold">
                      {phaseData[phase].name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground mb-2">Weeks {phaseData[phase].weeks}</p>
                    <p className="text-xs italic">"{phaseData[phase].message}"</p>
                    {isCompleted && (
                      <div className="mt-3 text-xs text-green-500 font-bold">✓ COMPLETED</div>
                    )}
                    {isCurrent && (
                      <div className="mt-3 text-xs font-bold text-primary">IN PROGRESS</div>
                    )}
                    {!isCurrent && !isCompleted && !isLocked && (
                      <div className="mt-3 text-xs font-bold text-cyan-400">🚀 UNLOCKED</div>
                    )}
                    {isClickable && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-3 w-full font-bold text-xs border-primary/40 hover:bg-primary hover:text-primary-foreground"
                        onClick={(e) => { e.stopPropagation(); openGeneratorForPhase(phase); }}
                        data-testid={`phase-${phase}-view-workouts-btn`}
                      >
                        <Sparkles className="w-3 h-3 mr-1" /> PICK & GENERATE
                      </Button>
                    )}
                    {!hasWorkouts && !isLocked && (
                      <p className="mt-3 text-xs text-muted-foreground italic">Post-program maintenance phase</p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Baseline & Test Results */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Baseline */}
          {baseline && (
            <Card className="bg-card border-border" data-testid="baseline-card">
              <CardHeader>
                <CardTitle className="text-xl font-heading font-bold text-white flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-accent" />
                  Baseline (Day 1)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-heading font-black text-white">{baseline.vertical_jump}"</p>
                    <p className="text-xs text-muted-foreground">Vertical</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-heading font-black text-white">{baseline.sprint_time}s</p>
                    <p className="text-xs text-muted-foreground">Sprint</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-heading font-black text-white">{baseline.lane_agility}s</p>
                    <p className="text-xs text-muted-foreground">Agility</p>
                  </div>
                </div>
                {baseline.notes && (
                  <p className="text-sm text-gray-300 mt-4 p-3 bg-background rounded-sm border border-border">
                    {baseline.notes}
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Phase Tests */}
          <Card className="bg-card border-border" data-testid="phase-tests-card">
            <CardHeader>
              <CardTitle className="text-xl font-heading font-bold text-white flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-primary" />
                Check-in Tests
              </CardTitle>
            </CardHeader>
            <CardContent>
              {phaseTests.length > 0 ? (
                <div className="space-y-3">
                  {phaseTests.map((test) => (
                    <div key={test.id} className="p-3 bg-background rounded-sm border border-border">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-bold text-white">Phase {test.phase} - Week {test.week}</span>
                        <span className="text-xs text-muted-foreground">
                          {new Date(test.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div>
                          <p className="text-lg font-mono font-bold text-primary">{test.vertical_jump}"</p>
                          <p className="text-xs text-muted-foreground">Vertical</p>
                        </div>
                        <div>
                          <p className="text-lg font-mono font-bold text-primary">{test.sprint_time}s</p>
                          <p className="text-xs text-muted-foreground">Sprint</p>
                        </div>
                        <div>
                          <p className="text-lg font-mono font-bold text-primary">{test.lane_agility}s</p>
                          <p className="text-xs text-muted-foreground">Agility</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <p className="text-sm">No check-in tests completed yet.</p>
                  <p className="text-xs mt-2">You'll test every 4 weeks (Weeks 4, 8, 12, 16)</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Elite Status */}
        {progress.current_week === 16 && (
          <Card className="bg-gradient-to-r from-accent/20 to-primary/20 border-accent">
            <CardContent className="p-8 text-center">
              <Trophy className="w-16 h-16 text-accent mx-auto mb-4" />
              <h3 className="text-3xl font-heading font-black text-white mb-2">ELITE STATUS UNLOCKED</h3>
              <p className="text-lg text-gray-300 mb-4">
                You've completed the 112-Day Transformation. You're now ready for Pivot Point Pro.
              </p>
              <Button className="bg-accent hover:bg-accent/90 text-black font-bold uppercase px-8 py-6 text-lg">
                Claim Your Badge
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      <WorkoutGenerator
        open={generatorOpen}
        onOpenChange={setGeneratorOpen}
        initialPhase={generatorPhase}
      />
    </div>
  );
};

export default ProgramProgress;